import { apiRequest } from '../../../shared/services/apiClient';
import type { EmailBrandPreview } from './emailBrandingService';

export type EmailLayoutBlockType = 'header' | 'content' | 'text' | 'button' | 'image' | 'divider' | 'spacer' | 'footer';
export type EmailLayoutAlign = 'left' | 'center' | 'right';

export interface EmailLayoutBlock {
  type: EmailLayoutBlockType;
  text?: string | null;
  label?: string | null;
  url?: string | null;
  imageUrl?: string | null;
  altText?: string | null;
  width?: number | null;
  height?: number | null;
  align?: EmailLayoutAlign | null;
  color?: string | null;
  backgroundColor?: string | null;
}

export interface EmailLayoutDocument {
  blocks: EmailLayoutBlock[];
}

export interface EmailLayoutSummary {
  id: string;
  name: string;
  isDefault: boolean;
  version: number;
  blockCount: number;
  updatedAt: string;
}

export interface EmailLayoutDetail {
  id: string;
  name: string;
  isDefault: boolean;
  version: number;
  document: EmailLayoutDocument;
  createdAt: string;
  updatedAt: string;
}

export interface EmailLayoutPreset {
  id: string;
  name: string;
  document: EmailLayoutDocument;
}

export interface EmailLayoutListResponse {
  layouts: EmailLayoutSummary[];
  presets: EmailLayoutPreset[];
}

export interface EmailLayoutTestResult {
  succeeded: boolean;
  message: string | null;
}

const BASE = '/api/v1/email/layouts';

const BLOCK_TYPES: EmailLayoutBlockType[] = ['header', 'content', 'text', 'button', 'image', 'divider', 'spacer', 'footer'];

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const unwrap = (payload: unknown): Record<string, unknown> => {
  const record = asRecord(payload);
  return record.data && typeof record.data === 'object' ? asRecord(record.data) : record;
};

const optionalString = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);
const optionalNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

export const normalizeEmailLayoutBlock = (value: unknown): EmailLayoutBlock | null => {
  const block = asRecord(value);
  const type = String(block.type ?? '').toLowerCase() as EmailLayoutBlockType;
  if (!BLOCK_TYPES.includes(type)) {
    return null;
  }
  const align = block.align === 'center' || block.align === 'right' || block.align === 'left' ? block.align : null;
  return {
    type,
    text: optionalString(block.text),
    label: optionalString(block.label),
    url: optionalString(block.url),
    imageUrl: optionalString(block.imageUrl),
    altText: optionalString(block.altText),
    width: optionalNumber(block.width),
    height: optionalNumber(block.height),
    align,
    color: optionalString(block.color),
    backgroundColor: optionalString(block.backgroundColor),
  };
};

export const normalizeEmailLayoutDocument = (value: unknown): EmailLayoutDocument => {
  const blocks = Array.isArray(asRecord(value).blocks) ? (asRecord(value).blocks as unknown[]) : [];
  return {
    blocks: blocks.map(normalizeEmailLayoutBlock).filter((block): block is EmailLayoutBlock => block !== null),
  };
};

export const normalizeEmailLayoutDetail = (payload: unknown): EmailLayoutDetail => {
  const data = unwrap(payload);
  return {
    id: String(data.id ?? ''),
    name: String(data.name ?? ''),
    isDefault: Boolean(data.isDefault),
    version: Number(data.version ?? 1),
    document: normalizeEmailLayoutDocument(data.document),
    createdAt: String(data.createdAt ?? ''),
    updatedAt: String(data.updatedAt ?? ''),
  };
};

export const normalizeEmailLayoutList = (payload: unknown): EmailLayoutListResponse => {
  const data = unwrap(payload);
  const layouts = Array.isArray(data.layouts) ? data.layouts : [];
  const presets = Array.isArray(data.presets) ? data.presets : [];
  return {
    layouts: layouts.map(asRecord).map(item => ({
      id: String(item.id ?? ''),
      name: String(item.name ?? ''),
      isDefault: Boolean(item.isDefault),
      version: Number(item.version ?? 1),
      blockCount: Number(item.blockCount ?? 0),
      updatedAt: String(item.updatedAt ?? ''),
    })),
    presets: presets.map(asRecord).map(item => ({
      id: String(item.id ?? ''),
      name: String(item.name ?? ''),
      document: normalizeEmailLayoutDocument(item.document),
    })),
  };
};

/** Nulls are left out so the server applies its own defaults instead of reading them as cleared. */
const serializeDocument = (document: EmailLayoutDocument) => ({
  blocks: document.blocks.map(block => Object.fromEntries(
    Object.entries(block).filter(([, value]) => value !== null && value !== undefined && value !== ''),
  )),
});

export const listEmailLayouts = async (): Promise<EmailLayoutListResponse> =>
  normalizeEmailLayoutList(await apiRequest<unknown>(BASE));

export const getEmailLayout = async (id: string): Promise<EmailLayoutDetail> =>
  normalizeEmailLayoutDetail(await apiRequest<unknown>(`${BASE}/${id}`));

export const createEmailLayout = async (request: { name: string; presetId?: string; document?: EmailLayoutDocument }): Promise<EmailLayoutDetail> =>
  normalizeEmailLayoutDetail(await apiRequest<unknown>(BASE, {
    method: 'POST',
    body: JSON.stringify({ ...request, document: request.document ? serializeDocument(request.document) : undefined }),
  }));

export const updateEmailLayout = async (id: string, name: string, document: EmailLayoutDocument): Promise<EmailLayoutDetail> =>
  normalizeEmailLayoutDetail(await apiRequest<unknown>(`${BASE}/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ name, document: serializeDocument(document) }),
  }));

export const deleteEmailLayout = async (id: string): Promise<void> => {
  await apiRequest<unknown>(`${BASE}/${id}`, { method: 'DELETE' });
};

export const duplicateEmailLayout = async (id: string): Promise<EmailLayoutDetail> =>
  normalizeEmailLayoutDetail(await apiRequest<unknown>(`${BASE}/${id}/duplicate`, { method: 'POST' }));

export const setDefaultEmailLayout = async (id: string): Promise<EmailLayoutDetail> =>
  normalizeEmailLayoutDetail(await apiRequest<unknown>(`${BASE}/${id}/default`, { method: 'POST' }));

export const previewEmailLayout = async (document: EmailLayoutDocument): Promise<EmailBrandPreview> => {
  const data = unwrap(await apiRequest<unknown>(`${BASE}/preview`, {
    method: 'POST',
    body: JSON.stringify({ document: serializeDocument(document) }),
  }));
  return {
    html: String(data.html ?? ''),
    text: String(data.text ?? ''),
    htmlBytes: Number(data.htmlBytes ?? 0),
    exceedsClipLimit: Boolean(data.exceedsClipLimit),
  };
};

export const testSendEmailLayout = async (id: string, to: string, channelAccountId?: string): Promise<EmailLayoutTestResult> => {
  const data = unwrap(await apiRequest<unknown>(`${BASE}/${id}/test-send`, {
    method: 'POST',
    body: JSON.stringify({ to, channelAccountId: channelAccountId || undefined }),
  }));
  return { succeeded: Boolean(data.succeeded), message: optionalString(data.message) };
};
